import { AssetTypes } from '@tassat/common-definitions';
import { formatAsAsset, formatAsCurrency } from '@tassat/utility';
import moment from 'moment';

import {
    ACG_ALL, PCG_ALL, assetUSD, currencyUSD
} from 'Lib/constant';

export const sortAlphabeticallyByField = (array, fieldName) => {
    if (array.length === 0) {
        return array;
    }

    return array.sort((a, b) => {
        if (a[fieldName].toLowerCase() < b[fieldName].toLowerCase()) {
            return -1;
        } else if (a[fieldName].toLowerCase() > b[fieldName].toLowerCase()) {
            return 1;
        }

        return 0;
    });
};

export const userNameSort = (userA, userB) => {
    if (userA.FirstName.toLowerCase() < userB.FirstName.toLowerCase()) {
        return -1;
    } else if (userA.FirstName.toLowerCase() > userB.FirstName.toLowerCase()) {
        return 1;
    } else if (userA.FirstName.toLowerCase() === userB.FirstName.toLowerCase()) {
        if (userA.LastName.toLowerCase() < userB.LastName.toLowerCase()) {
            return -1;
        } else if (userA.LastName.toLowerCase() > userB.LastName.toLowerCase()) {
            return 1;
        } else if (userA.LastName.toLowerCase() === userB.LastName.toLowerCase()) {
            return 0;
        }
    }
};

export function obscureAccountNo(acc) {
    if (acc === undefined) {
        return '';
    }

    // Start with 6 *'s
    let result = '******';
    const defaultShown = 4;
    const len = acc.length;

    if (len < 1) {
        result += '****';
    } else if (len <= 4) {
    // Only show 1 digit for short accounts
        result += '***';
        result += acc.slice(-1);
    } else {
    // Show default for longer
        result += acc.slice(-defaultShown);
    }

    // Result will always be 10 (even accounts longer than 10)
    return result;
}

export const formatNumberWithCommas = num => {
    if (typeof num === 'number') {
        num = num.toString();
    }

    const parts = num.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return parts.join('.');
};

export const removeEndingS = (string) => {
    if (string[string.length - 1] === 's') {
        return string.slice(0, string.length - 1);
    }

    return string;
};

export const sortPcgAlphaNumeric = (a, b, reverse = false) => {
    if (a === PCG_ALL['number']) return -1;
    if (b === PCG_ALL['number']) return 1;

    return reverse ? -(a.localeCompare(b)) : a.localeCompare(b);
};

export const sortAcgAlphaNumeric = (a, b, reverse = false) => {
    if (a === ACG_ALL['number']) return -1;
    if (b === ACG_ALL['number']) return 1;

    return reverse ? -(a.localeCompare(b)) : a.localeCompare(b);
};

export const formatEpochDate = (timeEpoch, format = 'MMM DD YYYY HH:mm:ss') => {
    if (!timeEpoch) return '';

    const date = moment(timeEpoch, 'x');
    if (date.isValid() && timeEpoch !== '0') return date.format(format);
    else return '';
};

export const formatMomentDate = (date) => {
    if (date.isValid()) return date.format('MMMM DD, YYYY');
    else return '';
};

export const formatMomentDateTime = (date, format = 'MMM DD YYYY HH:mm:ss') => {
    if (date.isValid()) return date.format(format);
    else return '';
};

export const printCurrencyStatistic = (value, currency, displayType) => {
    if (value || value === 0) {
        return formatAsCurrency(value, currency, displayType);
    } else return '-';
};

export const printAssetStatistic = (value, asset, displayType) => {
    if (value || value === 0) {
        return formatAsAsset(value, asset, displayType, undefined, true);
    } else return '-';
};

export const printNumberStatistic = (value) => {
    if (value || value === 0) {
        return formatNumberWithCommas(value);
    } else return '-';
};

export const caseInsensitiveSort = (valueA, valueB) => {
    //if value is not defined push to end of array for A or B
    if (!valueA) return 1;
    else if (!valueB) return -1;
    else return valueA.toLowerCase()
        .localeCompare(valueB.toLowerCase());
};

// Will print up to a 3 digit number with the magnitude after
// i.e. 100,000 as 100K or 2,000,000,000 as 2B
export const printNumberWithMagnitude = (value) => {
    // will only start showing magnitude character(s) after 1 thousand
    const magnitudeArray = [ '', 'K', 'M', 'B', 'T' ];
    let magnitude = 1;
    while (value >= 1000) {
        value = value / 1000;
        magnitude++;
    }

    if (value % 1 !== 0) {
        value = (Math.trunc(value * 10) / 10).toFixed(1);
    }

    return value + magnitudeArray[magnitude - 1];
};

export const camelCaseString = (value) => {
    if (typeof value === 'string') {
        return value
            .split(' ')
            .map((word) => word.slice(0, 1)
                .toUpperCase() + word.slice(1)
                .toLowerCase())
            .join(' ');
    }

    return value;
};

export const currencySort = (valueA, valueB) => {
    if (valueB.NumericCode === currencyUSD.NumericCode || valueB.AlphabeticCode === currencyUSD.AlphabeticCode) {
        return 1;
    }
    if (valueA.NumericCode === currencyUSD.NumericCode || valueA.AlphabeticCode === currencyUSD.AlphabeticCode) {
        return -1;
    }

    return valueA.AlphabeticCode < valueB.AlphabeticCode ? -1 : 1;
};

export const auditTimeSort = (recordA, recordB) => parseInt(recordB.AuditTime, 10) - parseInt(recordA.AuditTime, 10);

export const assetSort = (valueA, valueB) => {
    if (valueB.Type === AssetTypes.CURRENCY) {
        if (valueB.Curcode === assetUSD.Curcode) {
            return 1;
        }
        if (valueA.Curcode === assetUSD.Curcode) {
            return -1;
        }
    } else if (valueB.Type === AssetTypes.ASSET) {
        if (valueB.Curcode === assetUSD.Curcode) {
            return -1;
        }
        if (valueA.Curcode === assetUSD.Curcode) {
            return 1;
        }
    }

    return (valueA.Type === AssetTypes.CURRENCY ?
        valueA.Curcode : valueA.Ticker) < (valueB.Type === AssetTypes.CURRENCY ? valueB.Curcode : valueB.Ticker) ? -1 : 1;
};